import { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ORG_PUBLIC_PATH_PREFIX } from '@court-sync/shared';
import { publicOrgService } from '@/services/publicOrg';

const spinner = (
  <div className="flex min-h-[200px] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
  </div>
);

/**
 * Redirects legacy /academy/:academySlug/* and /club/:slug/* on subdomain hosts
 * to org-relative paths (/lessons, /court-hire, etc.).
 */
export function LegacyOrgPrefixRedirect({ legacyKind }: { legacyKind: 'academy' | 'club' }) {
  const location = useLocation();
  const params = useParams<{ academySlug?: string; slug?: string; '*': string }>();
  const legacySlug = legacyKind === 'academy' ? params.academySlug : params.slug;
  const { '*': rest } = params;
  const [orgSlug, setOrgSlug] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!legacySlug) {
      return undefined;
    }
    let mounted = true;
    const resolve =
      legacyKind === 'academy'
        ? publicOrgService.resolveOrgSlugFromAcademySlug(legacySlug)
        : publicOrgService.resolveOrgSlugFromClubSlug(legacySlug);
    void resolve.then((slug) => {
      if (mounted) setOrgSlug(slug);
    });
    return () => {
      mounted = false;
    };
  }, [legacyKind, legacySlug]);

  if (!legacySlug) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="text-xl font-semibold">Site not found</h1>
      </div>
    );
  }

  if (orgSlug === undefined) return spinner;
  if (!orgSlug) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="text-xl font-semibold">Site not found</h1>
      </div>
    );
  }

  const suffix = rest ? `/${rest}` : '';
  const target = suffix || '/';
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
}

/** Path-mode redirect from /academy/:slug to /club-academy/:orgSlug */
export function LegacyAcademyPathToOrgRedirect() {
  const location = useLocation();
  const { academySlug, '*': rest } = useParams<{ academySlug: string; '*': string }>();
  const [orgSlug, setOrgSlug] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!academySlug) {
      return undefined;
    }
    let mounted = true;
    void publicOrgService.resolveOrgSlugFromAcademySlug(academySlug).then((slug) => {
      if (mounted) setOrgSlug(slug);
    });
    return () => {
      mounted = false;
    };
  }, [academySlug]);

  if (!academySlug) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="text-xl font-semibold">Academy not found</h1>
      </div>
    );
  }

  if (orgSlug === undefined) return spinner;
  if (!orgSlug) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="text-xl font-semibold">Academy not found</h1>
      </div>
    );
  }

  const suffix = rest ? `/${rest}` : '';
  return (
    <Navigate
      to={`${ORG_PUBLIC_PATH_PREFIX}/${orgSlug}${suffix}${location.search}${location.hash}`}
      replace
    />
  );
}
